import React, { useCallback, useEffect, useState } from "react";
import {
  getPatientProfile,
  updatePatientProfile,
} from "../APIs/patientService";
import { deletePatientInsurance } from "../APIs/patientInsuranceApi";
import { getInsuranceDocuments } from "../APIs/patientDocumentApi";
import { useCurrentUser } from "../APIs/UserContext";
import AddressAutocomplete from "../Provider/AddressAutoComplete";
import AddEditInsuranceModal from "./AddEditInsuranceModal";
import InsuranceCardModal from "./InsuranceCardModal";

function StatusBadge({ status }) {
  const cfg = {
    VERIFIED: "bg-green-100 text-green-700",
    PENDING: "bg-yellow-100 text-yellow-700",
    REJECTED: "bg-red-100 text-red-600",
  };
  return (
    <span
      className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg[status] ?? cfg.PENDING}`}
    >
      {status ?? "PENDING"}
    </span>
  );
}

function CardPills({ hasFront, hasBack }) {
  return (
    <div className="flex items-center gap-1.5 mt-1.5">
      <span
        className={`text-xs px-2 py-0.5 rounded-full font-medium ${hasFront ? "bg-teal-100 text-teal-700" : "bg-gray-100 text-gray-400"}`}
      >
        Front {hasFront ? "✓" : "✗"}
      </span>
      <span
        className={`text-xs px-2 py-0.5 rounded-full font-medium ${hasBack ? "bg-teal-100 text-teal-700" : "bg-gray-100 text-gray-400"}`}
      >
        Back {hasBack ? "✓" : "✗"}
      </span>
    </div>
  );
}

const PatientInformation = ({ patientId }) => {
  const { refreshUser } = useCurrentUser();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [showEdit, setShowEdit] = useState(false);
  const [editErrors, setEditErrors] = useState({});
  const [editForm, setEditForm] = useState({
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    email: "",
    phoneNumber: "",
    addressLine: "",
    city: "",
    state: "",
    country: "",
    postalCode: "",
  });

  // Insurance modal flow: step 1 = form, step 2 = card upload
  const [insModal, setInsModal] = useState(null); // null | { mode, insurance, step }
  const [confirmDeleteIns, setConfirmDeleteIns] = useState(null);
  const [deletingIns, setDeletingIns] = useState(false);
  const [cardStatuses, setCardStatuses] = useState({}); // { [insuranceId]: { hasFront, hasBack } }

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const data = await getPatientProfile(patientId);
      setProfile(data);
      // Load card statuses for each insurance in parallel
      const ins = data?.insurances ?? [];
      if (ins.length > 0) {
        const results = await Promise.allSettled(
          ins.map((i) => getInsuranceDocuments(i.id)),
        );
        const statuses = {};
        ins.forEach((i, idx) => {
          const docs =
            results[idx].status === "fulfilled" ? results[idx].value : [];
          statuses[i.id] = {
            hasFront: docs.some((d) => d.category === "INSURANCE_CARD_FRONT"),
            hasBack: docs.some((d) => d.category === "INSURANCE_CARD_BACK"),
          };
        });
        setCardStatuses(statuses);
      }
    } catch {
      setErr("Failed to load profile");
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const formatPhone = (value = "") => {
    const digits = value.replace(/\D/g, "").slice(0, 10);
    const p1 = digits.slice(0, 3);
    const p2 = digits.slice(3, 6);
    const p3 = digits.slice(6, 10);
    if (digits.length <= 3) return p1;
    if (digits.length <= 6) return `(${p1}) ${p2}`;
    return `(${p1}) ${p2}-${p3}`;
  };

  const openEdit = () => {
    if (!profile?.patient) return;
    const p = profile.patient;
    setEditForm({
      firstName: p.firstName || "",
      lastName: p.lastName || "",
      dateOfBirth: p.dateOfBirth || "",
      email: p.email || "",
      phoneNumber: formatPhone(p.phoneNumber || ""),
      addressLine: p.addressLine || "",
      city: p.city || "",
      state: p.state || "",
      country: p.country || "",
      postalCode: p.postalCode || "",
    });
    setEditErrors({});
    setShowEdit(true);
  };

  const validateEditForm = () => {
    const nextErrors = {};
    const namePattern = /^[A-Za-z0-9][A-Za-z0-9 '-]*$/;
    const phoneDigits = editForm.phoneNumber.replace(/\D/g, "");

    if (!editForm.firstName.trim()) {
      nextErrors.firstName = "First name is required";
    } else if (!namePattern.test(editForm.firstName.trim())) {
      nextErrors.firstName =
        "Use letters, numbers, spaces, apostrophes, or hyphens";
    }

    if (!editForm.lastName.trim()) {
      nextErrors.lastName = "Last name is required";
    } else if (!namePattern.test(editForm.lastName.trim())) {
      nextErrors.lastName =
        "Use letters, numbers, spaces, apostrophes, or hyphens";
    }

    if (!phoneDigits) {
      nextErrors.phoneNumber = "Phone number is required";
    } else if (phoneDigits.length !== 10) {
      nextErrors.phoneNumber = "Phone number must be 10 digits";
    }

    if (!editForm.addressLine.trim()) {
      nextErrors.addressLine = "Address is required";
    }

    if (!editForm.dateOfBirth) {
      nextErrors.dateOfBirth = "Date of birth is required";
    }

    setEditErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const renderEditError = (field) =>
    editErrors[field] ? (
      <div className="mt-1 text-xs text-red-600">{editErrors[field]}</div>
    ) : null;

  const submitEdit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!validateEditForm()) return;

    const payload = {
      ...editForm,
      firstName: editForm.firstName.trim(),
      lastName: editForm.lastName.trim(),
      phoneNumber: editForm.phoneNumber.replace(/\D/g, ""),
      addressLine: editForm.addressLine.trim(),
    };

    try {
      const updated = await updatePatientProfile(patientId, {
        patientUpdate: payload,
      });
      setProfile(updated);
      await refreshUser?.();
      setShowEdit(false);
      setEditErrors({});
    } catch {
      setErr("Update failed");
    }
  };

  const handleDeleteInsurance = async (ins) => {
    setDeletingIns(true);
    try {
      await deletePatientInsurance(patientId, ins.id);
      setConfirmDeleteIns(null);
      await loadProfile();
    } catch {
      setErr("Failed to delete insurance");
    } finally {
      setDeletingIns(false);
    }
  };

  // After Modal 1 saves: advance to step 2 (card upload)
  const handleInsuranceSaved = (savedIns) => {
    setInsModal((prev) => ({ ...prev, insurance: savedIns, step: 2 }));
  };

  // After Modal 2 (cards) finishes
  const handleCardsDone = () => {
    setInsModal(null);
    loadProfile();
  };

  if (loading) return <div className="p-4 text-gray-500">Loading…</div>;
  if (err) return <div className="p-4 text-red-600">{err}</div>;
  if (!profile) return null;

  const { patient, insurances = [] } = profile;
  const usedCategories = insurances.map((i) => i.category).filter(Boolean);
  const canAddMore = insurances.length < 2;

  return (
    <div className="max-w-3xl mx-auto p-4 space-y-6">
      {/* ── Patient Profile ── */}
      <section className="bg-white border rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-lg text-gray-900">Patient Profile</h2>
          <button
            onClick={openEdit}
            className="px-3 py-1.5 rounded-lg bg-teal-600 text-white text-sm hover:bg-teal-700"
          >
            Edit
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-6 text-sm text-gray-700">
          <div>
            <span className="font-medium text-gray-500">First Name</span>
            <p>{patient.firstName}</p>
          </div>
          <div>
            <span className="font-medium text-gray-500">Last Name</span>
            <p>{patient.lastName}</p>
          </div>
          <div>
            <span className="font-medium text-gray-500">DOB</span>
            <p>{patient.dateOfBirth}</p>
          </div>
          <div>
            <span className="font-medium text-gray-500">Email</span>
            <p>{patient.email}</p>
          </div>
          <div>
            <span className="font-medium text-gray-500">Phone</span>
            <p>{patient.phoneNumber}</p>
          </div>
          <div>
            <span className="font-medium text-gray-500">Address</span>
            <p>{patient.addressLine}</p>
          </div>
          <div>
            <span className="font-medium text-gray-500">City</span>
            <p>{patient.city}</p>
          </div>
          <div>
            <span className="font-medium text-gray-500">State</span>
            <p>{patient.state}</p>
          </div>
          <div>
            <span className="font-medium text-gray-500">ZIP</span>
            <p>{patient.postalCode}</p>
          </div>
          <div>
            <span className="font-medium text-gray-500">Country</span>
            <p>{patient.country}</p>
          </div>
        </div>
      </section>

      {/* ── Insurance ── */}
      <section className="bg-white border rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-lg text-gray-900">Insurance</h2>
          {canAddMore && (
            <button
              onClick={() =>
                setInsModal({ mode: "add", insurance: null, step: 1 })
              }
              className="px-3 py-1.5 rounded-lg bg-teal-600 text-white text-sm hover:bg-teal-700"
            >
              + Add Insurance
            </button>
          )}
        </div>

        {insurances.length === 0 ? (
          <p className="text-sm text-gray-500">No insurance on file.</p>
        ) : (
          <div className="space-y-3">
            {insurances.map((ins) => {
              const cards = cardStatuses[ins.id] ?? {
                hasFront: false,
                hasBack: false,
              };
              const isConfirming = confirmDeleteIns === ins.id;

              return (
                <div key={ins.id} className="border rounded-xl p-4 text-sm">
                  {/* Row header: category + status + actions */}
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ins.category === "PRIMARY" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}`}
                      >
                        {ins.category}
                      </span>
                      <StatusBadge status={ins.status} />
                    </div>

                    {/* Actions */}
                    {isConfirming ? (
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          disabled={deletingIns}
                          onClick={() => handleDeleteInsurance(ins)}
                          className="text-xs px-2 py-1 rounded bg-red-600 text-white disabled:opacity-40"
                        >
                          {deletingIns ? "..." : "Confirm"}
                        </button>
                        <button
                          onClick={() => setConfirmDeleteIns(null)}
                          className="text-xs px-2 py-1 rounded border"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() =>
                            setInsModal({
                              mode: "edit",
                              insurance: ins,
                              step: 1,
                            })
                          }
                          className="text-xs text-teal-600 border border-teal-600 rounded px-2 py-0.5 hover:bg-teal-50"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setConfirmDeleteIns(ins.id)}
                          className="text-xs text-red-500 border border-red-200 rounded px-2 py-0.5 hover:bg-red-50"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Plan info */}
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-gray-700">
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide">
                        Plan
                      </p>
                      <p className="font-medium">
                        {ins.planName || ins.planId}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide">
                        Member ID
                      </p>
                      <p>{ins.memberId}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide">
                        Group ID
                      </p>
                      <p>{ins.groupId || "—"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide">
                        Coverage
                      </p>
                      <p>
                        {ins.coverageStart || "—"} → {ins.coverageEnd || "—"}
                      </p>
                    </div>
                  </div>

                  {/* Card pills */}
                  <CardPills
                    hasFront={cards.hasFront}
                    hasBack={cards.hasBack}
                  />
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── Modals ── */}

      {/* Step 1: Insurance form */}
      {insModal?.step === 1 && (
        <AddEditInsuranceModal
          patientId={patientId}
          insurance={insModal.insurance}
          usedCategories={usedCategories}
          onClose={() => setInsModal(null)}
          onSaved={handleInsuranceSaved}
        />
      )}

      {/* Step 2: Card upload */}
      {insModal?.step === 2 && insModal.insurance && (
        <InsuranceCardModal
          patientId={patientId}
          insurance={insModal.insurance}
          onClose={handleCardsDone}
          onSaved={handleCardsDone}
        />
      )}

      {/* Edit patient info modal */}
      {showEdit && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-lg">Edit Patient Information</h3>
              <button
                onClick={() => {
                  setShowEdit(false);
                  setErr("");
                  setEditErrors({});
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            <form
              onSubmit={submitEdit}
              className="p-6 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm"
            >
              {[
                { name: "firstName", placeholder: "First Name" },
                { name: "lastName", placeholder: "Last Name" },
                { name: "email", placeholder: "Email", type: "email" },
              ].map((f) => (
                <div key={f.name}>
                  <input
                    name={f.name}
                    type={f.type || "text"}
                    value={editForm[f.name]}
                    onChange={(e) =>
                      setEditForm((p) => ({ ...p, [f.name]: e.target.value }))
                    }
                    placeholder={f.placeholder}
                    disabled={f.name === "email"}
                    className={`border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                      f.name === "email"
                        ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                        : ""
                    }`}
                  />
                  {renderEditError(f.name)}
                </div>
              ))}
              <div>
                <input
                  name="phoneNumber"
                  type="tel"
                  inputMode="tel"
                  value={editForm.phoneNumber}
                  onChange={(e) =>
                    setEditForm((p) => ({
                      ...p,
                      phoneNumber: formatPhone(e.target.value),
                    }))
                  }
                  placeholder="Phone"
                  maxLength={14}
                  className="border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                {renderEditError("phoneNumber")}
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs text-gray-500 mb-1">
                  Address
                </label>
                <AddressAutocomplete
                  value={editForm.addressLine}
                  placeholder="Start typing address..."
                  onSelect={(addr) => {
                    setEditForm((p) => ({
                      ...p,
                      addressLine: addr?.addressLine || p.addressLine,
                      city: addr?.city || "",
                      state: addr?.state || "",
                      postalCode: addr?.postalCode || "",
                      country: addr?.country || "",
                    }));
                  }}
                />
                {renderEditError("addressLine")}
                {(editForm.city ||
                  editForm.state ||
                  editForm.postalCode ||
                  editForm.country) && (
                  <div className="mt-2 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600">
                    {[
                      editForm.city,
                      editForm.state,
                      editForm.postalCode,
                      editForm.country,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </div>
                )}
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs text-gray-500 mb-1">
                  Date of Birth
                </label>
                <input
                  name="dateOfBirth"
                  type="date"
                  value={editForm.dateOfBirth}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, dateOfBirth: e.target.value }))
                  }
                  className="border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                {renderEditError("dateOfBirth")}
              </div>
              <div className="md:col-span-2 flex justify-end gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowEdit(false);
                    setErr("");
                    setEditErrors({});
                  }}
                  className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-50 text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-teal-600 text-white text-sm hover:bg-teal-700"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientInformation;
